/**
 * Client-side GPS for uploads:
 * - Optional per-file source from MultiPhotoCapture: `library` = EXIF only; `camera` = device location, EXIF fallback.
 * - Legacy (no sources): EXIF per file, then browser Geolocation only where EXIF is missing.
 */
import exifr from "exifr";

export type GpsHintJson = { lat: number; lng: number } | null;

/** From camera button vs files/gallery button — drives GPS hint strategy */
export type GpsUploadSource = "camera" | "library";

function validCoord(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/** exifr.gps on a single input (File or ArrayBuffer), validated. */
async function gpsFromInput(input: File | ArrayBuffer): Promise<GpsHintJson> {
  try {
    const gps = await exifr.gps(input);
    if (gps && typeof gps.latitude === "number" && typeof gps.longitude === "number") {
      if (validCoord(gps.latitude, gps.longitude)) {
        return { lat: gps.latitude, lng: gps.longitude };
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Try EXIF GPS: Blob/File first, then ArrayBuffer fallback.
 * iOS/HEIC: the ArrayBuffer path frequently succeeds where the File path
 * fails (Safari sometimes only exposes EXIF via the decoded buffer).
 */
async function gpsFromExifOnly(file: File): Promise<GpsHintJson> {
  const fromFile = await gpsFromInput(file);
  if (fromFile) return fromFile;
  try {
    const buf = await file.arrayBuffer();
    return await gpsFromInput(buf);
  } catch {
    return null;
  }
}

/** One entry per file, same order as FormData `files` — EXIF only (parallel per file) */
export async function buildGpsHintsFromExifOnly(files: File[]): Promise<GpsHintJson[]> {
  return Promise.all(files.map((file) => gpsFromExifOnly(file)));
}

/** Same bounds as server `parse-captured-at-hints` / `exif-gps` */
export const CAPTURE_YEAR_MIN = 1980;
export const CAPTURE_YEAR_MAX = 2100;

/** UTC ISO string if date is finite and year in capture range; else null */
export function clientDateToIsoUtc(d: Date): string | null {
  const t = d.getTime();
  if (!Number.isFinite(t)) return null;
  const y = d.getUTCFullYear();
  if (y < CAPTURE_YEAR_MIN || y > CAPTURE_YEAR_MAX) return null;
  return new Date(t).toISOString();
}

/** Parse capture-date tags from a single input (File or ArrayBuffer). */
async function captureDateFromInput(input: File | ArrayBuffer): Promise<string | null> {
  try {
    const tags = await exifr.parse(input, {
      pick: ["DateTimeOriginal", "CreateDate", "ModifyDate"],
      reviveValues: true,
    });
    if (!tags || typeof tags !== "object") return null;
    const t = tags as Record<string, unknown>;
    for (const c of [t.DateTimeOriginal, t.CreateDate, t.ModifyDate]) {
      if (c instanceof Date) {
        const iso = clientDateToIsoUtc(c);
        if (iso) return iso;
      }
      if (typeof c === "string" && c.trim()) {
        const iso = clientDateToIsoUtc(new Date(c));
        if (iso) return iso;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Best-effort EXIF capture time (DateTimeOriginal, CreateDate, ModifyDate) — client only.
 * Blob/File first, then ArrayBuffer fallback (same iOS/HEIC reasoning as GPS).
 */
async function captureDateFromExifOnly(file: File): Promise<string | null> {
  const fromFile = await captureDateFromInput(file);
  if (fromFile) return fromFile;
  try {
    const buf = await file.arrayBuffer();
    return await captureDateFromInput(buf);
  } catch {
    return null;
  }
}

/**
 * Parallel to `files` when `sources` matches length.
 * Camera: `null` (server leaves captured_at unset — sort by upload time).
 * Library: EXIF-derived ISO, or `""` so server falls back to its own EXIF parse.
 */
export async function buildCapturedAtHintsForFilesWithSources(
  files: File[],
  sources: GpsUploadSource[]
): Promise<Array<string | null>> {
  if (files.length === 0 || sources.length !== files.length) return [];
  return Promise.all(
    files.map(async (file, i) => {
      if (sources[i] === "camera") return null;
      const iso = await captureDateFromExifOnly(file);
      return iso ?? "";
    })
  );
}

const GEO_CACHE_MS = 120_000;
let geoCache: { lat: number; lng: number; expires: number } | null = null;
let inFlightGeo: Promise<GpsHintJson> | null = null;

/**
 * Single shared in-flight request + short cache so rapid sequential uploads (e.g. DealExtras)
 * do not spam getCurrentPosition.
 */
export function getBrowserGeolocationFallback(): Promise<GpsHintJson> {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.resolve(null);
  }

  const now = Date.now();
  if (geoCache && geoCache.expires > now) {
    return Promise.resolve({ lat: geoCache.lat, lng: geoCache.lng });
  }

  if (inFlightGeo) return inFlightGeo;

  inFlightGeo = new Promise<GpsHintJson>((resolve) => {
    const clear = () => {
      inFlightGeo = null;
    };
    const timer = window.setTimeout(() => {
      clear();
      resolve(null);
    }, 12_000);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        window.clearTimeout(timer);
        clear();
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        if (validCoord(lat, lng)) {
          geoCache = { lat, lng, expires: Date.now() + GEO_CACHE_MS };
          resolve({ lat, lng });
        } else {
          resolve(null);
        }
      },
      () => {
        window.clearTimeout(timer);
        clear();
        resolve(null);
      },
      {
        enableHighAccuracy: false,
        maximumAge: 300_000,
        timeout: 10_000,
      }
    );
  });

  return inFlightGeo;
}

/**
 * Per-file source from UI: library picks use only EXIF (no location permission).
 * Camera picks use device position, then EXIF if geolocation denied/unavailable.
 */
export async function buildGpsHintsForFilesWithSources(
  files: File[],
  sources: GpsUploadSource[]
): Promise<GpsHintJson[]> {
  if (files.length === 0) return [];
  if (sources.length !== files.length) {
    return buildGpsHintsForFiles(files);
  }

  const exifHints = await buildGpsHintsFromExifOnly(files);
  const needsDeviceGeo = sources.some((s) => s === "camera");
  const geo = needsDeviceGeo ? await getBrowserGeolocationFallback() : null;

  return files.map((_, i) => {
    if (sources[i] === "library") {
      return exifHints[i];
    }
    return geo ?? exifHints[i];
  });
}

/**
 * Unknown pick source: treated as `library` — EXIF only, NO device-geolocation fallback.
 * (Stamping the uploader's current position onto a picked/dropped photo that lacks EXIF GPS
 * produced wrong locations; only the explicit camera button uses device GPS.)
 */
export async function buildGpsHintsForFiles(files: File[]): Promise<GpsHintJson[]> {
  if (files.length === 0) return [];
  return buildGpsHintsFromExifOnly(files);
}

/**
 * Appends `gpsHints` and `capturedAtHints` (always) to the FormData.
 * When `sources` is omitted, every file is treated as `library` (EXIF only):
 *  - GPS: EXIF, else null (never the uploader's device location)
 *  - capture time: EXIF ISO, else "" so the server can try its own EXIF parse
 * Camera picks (explicit source) still use device GPS + leave capture time unset.
 */
export async function appendGpsHintsToFormData(
  formData: FormData,
  files: File[],
  sources?: GpsUploadSource[]
): Promise<void> {
  if (files.length === 0) return;
  const effectiveSources: GpsUploadSource[] =
    sources && sources.length === files.length ? sources : files.map(() => "library");
  const hints = await buildGpsHintsForFilesWithSources(files, effectiveSources);
  formData.append("gpsHints", JSON.stringify(hints));
  const capturedAtHints = await buildCapturedAtHintsForFilesWithSources(files, effectiveSources);
  formData.append("capturedAtHints", JSON.stringify(capturedAtHints));
}
